// A small translation module. English ships with the app; the other languages are loaded
// on demand (their own chunks), before the first render when the player needs them.
//
// t("key", { name: "Sam" })   -> the string with {name} filled in
// t("key", { count: 1 })      -> the singular form from the language's ONE (locales/*.ts) when the
//                                number ({count}, or {n} outside English) takes it
// tx("key", { link: <a/> })   -> the same, with React elements in place of the {placeholders}
//
// Language: ?lang=es|pt|fr|de|it|pl|tr|id|ar|zh-CN|ja|ko|ru|he|en in the URL (saved), then a saved choice, then the portal's language
// (Playgama, once its SDK is up), then the browser's languages, else English.
import React from "react";
import en, { ONE } from "./locales/en";

export type Lang = "en" | "es" | "pt" | "fr" | "de" | "it" | "pl" | "tr" | "id" | "ja" | "ko" | "zh-CN" | "ru" | "he" | "ar";
export type Key = keyof typeof en;
export type Dict = { [K in Key]: string };
type Params = { [name: string]: string | number | undefined };

export const LANGS: Lang[] = ["en", "es", "pt", "fr", "de", "it", "pl", "tr", "id", "ja", "ko", "zh-CN", "ru", "he", "ar"];
export const LANG_LABELS: { [l in Lang]: string } = { en: "English", es: "Español", pt: "Português", fr: "Français", de: "Deutsch", it: "Italiano", pl: "Polski", tr: "Türkçe", id: "Bahasa Indonesia", ja: "日本語", ko: "한국어", "zh-CN": "简体中文", ru: "Русский", he: "עברית", ar: "العربية" };
// Languages written right to left: the page gets dir="rtl" (the card table keeps its own layout).
export const isRtl = (l: Lang = lang): boolean => l === "he" || l === "ar";
const STORAGE_KEY = "rafiki_lang";

let lang: Lang = "en";
let dict: Dict = en;
const dicts: { [l in Lang]?: Dict } = { en };
type Ones = { [K in Key]?: string };
// Forms for a number that takes the singular, from each language's ONE (English: count only).
const ones: { [l in Lang]?: Ones } = { en: ONE };
// Whether a number takes the singular: 1 in English, Spanish, Portuguese, German and Italian; below 2 in French ("0 point", "-1 point").
// Polish and Arabic have no ONE list: their strings put numbers where the noun doesn't change ("Punkty: 3", "pkt").
const isOne = (l: Lang, v: unknown): boolean => {
  const n = Number(v);
  if (v === undefined || v === null || v === "" || Number.isNaN(n)) return false;
  // -1 takes the singular too ("-1 point"; Sam's playtest 5 Nov 2026), where the singular
  // form still shows the number (see t()).
  return l === "fr" ? Math.abs(n) < 2 : Math.abs(n) === 1;
};
const listeners: Array<(l: Lang) => void> = [];
// Playgama: the language comes only from the Bridge (platform.language), never from the browser,
// and nothing is written to localStorage (Playgama requirements 3.4.2 and 3.2.1).
const PLATFORM_LANG_ONLY = process.env.REACT_APP_PORTAL === "playgama";

let explicit = false; // the player (or the URL) chose; the portal's language doesn't override that

export const getLang = (): Lang => lang;
// House-rule presets not offered in a language. "Gringo" can read as a slur, so the Japanese and
// Korean game doesn't offer it (an existing Gringo room still plays, under its own name).
export const hiddenPresets = (l: Lang = lang): string[] => (l === "en" ? [] : ["gringo"]);
// The page's <html lang>: the site has always said en-GB.
// Portuguese is Brazilian Portuguese; Chinese is Simplified (zh-CN).
export const htmlLang = (l: Lang = lang): string => (l === "en" ? "en-GB" : l === "pt" ? "pt-BR" : l);

// In a right-to-left language, a filled-in name or number is wrapped in Unicode isolates (FSI…PDI)
// so a Latin name ("Sam!") or "-1" keeps its own direction inside the Hebrew sentence. Links are left as they are.
const isolate = (v: string) => (isRtl() && v && !/^https?:/.test(v) ? `\u2068${v}\u2069` : v);
const fill = (s: string, params?: Params) =>
  params ? s.replace(/\{(\w+)\}/g, (m, k) => (params[k] === undefined ? m : isolate(String(params[k])))) : s;

export function t(key: Key, params?: Params): string {
  let s: string | undefined;
  const one = ones[lang];
  if (one && params) {
    const v = params.count !== undefined ? params.count : lang === "en" ? undefined : params.n;
    // A singular form without the number in it ("נקודה אחת" = "one point") is for 1 only.
    if (isOne(lang, v) && one[key] !== undefined && (Number(v) === 1 || /\{(count|n)\}/.test(one[key] as string))) s = one[key];
  }
  if (s === undefined) s = dict[key];
  if (s === undefined) s = en[key];
  return fill(s === undefined ? String(key) : s, params);
}

// As t(), but placeholders may be React elements (links, bold text, icons).
export function tx(key: Key, params: { [name: string]: React.ReactNode }): React.ReactNode {
  const s = t(key);
  const parts = s.split(/\{(\w+)\}/g);
  return React.createElement(
    React.Fragment,
    null,
    ...parts.map((p, i) =>
      i % 2 === 1
        ? React.createElement(React.Fragment, { key: i }, p in params ? params[p] : `{${p}}`)
        : p
    )
  );
}

// Names the game stores in English for the player and the computer players.
export const nm = (name?: string): string => {
  if (!name) return "";
  if (name === "You") return t("common.you");
  const m = /^Computer(?: (\d+))?$/.exec(name);
  if (m) return m[1] ? t("common.computerN", { n: m[1] }) : t("common.computer");
  return name;
};

// A house-rules preset's name ("Cabo"; カーボ in Japanese). Unknown ids come back as given.
export const presetName = (id: string): string => {
  const key = `preset.${id}`;
  return key in en ? t(key as Key) : id;
};

// "A, B and C" in the current language.
export const joinNames = (names: (string | undefined)[]): string => {
  const list = names.map((n) => n || "");
  if (list.length <= 1) return list[0] || "";
  return t("common.andList", { list: list.slice(0, -1).join(t("common.listSep")), last: list[list.length - 1] });
};

const asLang = (v?: string | null): Lang | undefined => {
  const s = String(v || "").toLowerCase();
  if (s.startsWith("es")) return "es";
  if (s.startsWith("pt")) return "pt";
  if (s.startsWith("fr")) return "fr";
  if (s.startsWith("de")) return "de";
  if (s.startsWith("it")) return "it";
  if (s.startsWith("pl")) return "pl";
  if (s.startsWith("tr")) return "tr";
  if (s.startsWith("id") || s === "in" || s.startsWith("in-") || s.startsWith("in_")) return "id"; // "in": the old code for Indonesian
  if (s.startsWith("ar")) return "ar";
  if (s.startsWith("zh")) return "zh-CN"; // zh, zh-CN, zh-Hans, zh-SG (and zh-TW/zh-HK: Simplified is closer than English)
  if (s.startsWith("ja")) return "ja";
  if (s.startsWith("ko")) return "ko";
  if (s.startsWith("ru")) return "ru";
  if (s.startsWith("he") || s.startsWith("iw")) return "he";
  if (s.startsWith("en")) return "en";
  return undefined;
};

const read = (): Lang | undefined => {
  if (PLATFORM_LANG_ONLY) return undefined;
  try {
    return asLang(window.localStorage.getItem(STORAGE_KEY));
  } catch (e) {
    return undefined;
  }
};
const save = (l: Lang) => {
  if (PLATFORM_LANG_ONLY) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, l);
  } catch (e) {}
};

// ?lang= on the page's URL or on the hash route (portal builds); removed once read so it
// doesn't get in the way of ?room= links.
const fromUrl = (): Lang | undefined => {
  try {
    const search = new URLSearchParams(window.location.search);
    const [hashPath, hashQuery = ""] = window.location.hash.split("?");
    const hash = new URLSearchParams(hashQuery);
    const l = asLang(search.get("lang") || hash.get("lang"));
    if (search.has("lang") || hash.has("lang")) {
      search.delete("lang");
      hash.delete("lang");
      const s = search.toString();
      const h = hash.toString();
      const url =
        window.location.pathname + (s ? `?${s}` : "") + (window.location.hash ? hashPath + (h ? `?${h}` : "") : "");
      window.history.replaceState(window.history.state, "", url);
    }
    return l;
  } catch (e) {
    return undefined;
  }
};

const fromBrowser = (): Lang => {
  try {
    const list = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language]) || [];
    for (const l of list) {
      const found = asLang(l);
      if (found) return found;
    }
  } catch (e) {}
  return "en";
};

const load = async (l: Lang): Promise<Dict> => {
  if (dicts[l]) return dicts[l] as Dict;
  const mod: { default: Dict; ONE?: Ones } =
    l === "es"
      ? await import(/* webpackChunkName: "lang-es" */ "./locales/es")
      : l === "pt"
      ? await import(/* webpackChunkName: "lang-pt" */ "./locales/pt")
      : l === "fr"
      ? await import(/* webpackChunkName: "lang-fr" */ "./locales/fr")
      : l === "ja"
      ? await import(/* webpackChunkName: "lang-ja" */ "./locales/ja")
      : l === "ru"
      ? await import(/* webpackChunkName: "lang-ru" */ "./locales/ru")
      : l === "he"
      ? await import(/* webpackChunkName: "lang-he" */ "./locales/he")
      : l === "de"
      ? await import(/* webpackChunkName: "lang-de" */ "./locales/de")
      : l === "it"
      ? await import(/* webpackChunkName: "lang-it" */ "./locales/it")
      : l === "pl"
      ? await import(/* webpackChunkName: "lang-pl" */ "./locales/pl")
      : l === "tr"
      ? await import(/* webpackChunkName: "lang-tr" */ "./locales/tr")
      : l === "id"
      ? await import(/* webpackChunkName: "lang-id" */ "./locales/id")
      : l === "ar"
      ? await import(/* webpackChunkName: "lang-ar" */ "./locales/ar")
      : l === "zh-CN"
      ? await import(/* webpackChunkName: "lang-zh-CN" */ "./locales/zh-CN")
      : await import(/* webpackChunkName: "lang-ko" */ "./locales/ko");
  if (mod.ONE) ones[l] = mod.ONE;
  dicts[l] = mod.default;
  return mod.default;
};

const apply = (l: Lang, d: Dict) => {
  lang = l;
  dict = d;
  try {
    document.documentElement.lang = htmlLang(l);
    document.documentElement.dir = isRtl(l) ? "rtl" : "ltr";
  } catch (e) {}
  listeners.forEach((fn) => fn(l));
};

let wanted: Lang = "en"; // the latest language asked for (a slow download mustn't win over a later choice)
export async function setLang(l: Lang, opts: { remember?: boolean } = {}): Promise<void> {
  if (opts.remember !== false) {
    explicit = true;
    save(l);
  }
  wanted = l;
  try {
    const d = await load(l);
    if (wanted === l) apply(l, d);
  } catch (e) {
    if (wanted === l) apply("en", en); // the strings didn't load (offline): English rather than nothing
  }
}

export const onLangChange = (fn: (l: Lang) => void) => {
  listeners.push(fn);
  return () => {
    const i = listeners.indexOf(fn);
    if (i >= 0) listeners.splice(i, 1);
  };
};

// Before the first render: pick the language and load its strings.
export function initLang(): Promise<void> {
  const url = fromUrl();
  if (url) {
    save(url);
    explicit = true;
  }
  const saved = url || read();
  if (saved) explicit = true;
  const l = saved || (PLATFORM_LANG_ONLY ? "en" : fromBrowser());
  if (l === "en") {
    wanted = "en";
    apply("en", en);
    return Promise.resolve();
  }
  return setLang(l, { remember: false });
}

// The portal's own language setting (Playgama's bridge.platform.language), once its SDK is up.
// Ignored when the player or the URL already chose. Not applied mid-game: the table keeps its
// language and the change happens when the player next leaves it.
export function applyPlatformLang(value?: string) {
  const l = asLang(value);
  if (!l || explicit || l === wanted) return;
  const inGame = () => /\/game\//.test(window.location.pathname + window.location.hash);
  if (!inGame()) {
    setLang(l, { remember: false });
    return;
  }
  const later = () => {
    if (inGame()) return;
    window.removeEventListener("hashchange", later);
    window.removeEventListener("popstate", later);
    if (!explicit) setLang(l, { remember: false });
  };
  window.addEventListener("hashchange", later);
  window.addEventListener("popstate", later);
}

// For tests.
export const _resetLang = () => {
  explicit = false;
  wanted = "en";
  apply("en", en);
};
